import { useEffect, useRef } from 'react';
import { debounce } from 'lodash';

/**
 * Custom hook for auto-saving event data
 * @param {Object} eventData - Current event data
 * @param {string} eventID - Event ID
 * @param {Function} saveFunction - Function to call for saving
 * @param {number} delay - Debounce delay in milliseconds (default: 2000)
 * @param {boolean} enabled - Whether auto-save is enabled (default: true)
 */
export const useAutoSave = (
  eventData,
  eventID,
  saveFunction,
  delay = 20000,
  enabled = true
) => {
  const isInitialMount = useRef(true);
  const lastSavedRef = useRef(null);
  const saveCountRef = useRef(0);

  useEffect(() => {
    if (!enabled || !eventID || !saveFunction) return;

    // Skip on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      lastSavedRef.current = JSON.stringify(eventData);
      return;
    }

    const currentData = JSON.stringify(eventData);
    
    // Skip if data hasn't changed
    if (currentData === lastSavedRef.current) return;

    // Create debounced save function
    const debouncedSave = debounce(async () => {
      try {
        saveCountRef.current += 1;
        const saveNumber = saveCountRef.current;
        
        if (process.env.NODE_ENV === 'development') {
          console.log(`[Auto-save #${saveNumber}] Saving...`);
        }

        await saveFunction(eventData);
        
        lastSavedRef.current = currentData;
        
        if (process.env.NODE_ENV === 'development') {
          console.log(`[Auto-save #${saveNumber}] Saved successfully`);
        }
      } catch (error) {
        console.error('[Auto-save] Failed to save:', error);
        // Optionally show a toast notification
        // dangerMessage('Auto-save failed. Please save manually.');
      }
    }, delay);

    debouncedSave();

    // Cleanup function
    return () => {
      debouncedSave.cancel();
    };
  }, [eventData, eventID, saveFunction, delay, enabled]);

  return {
    saveCount: saveCountRef.current,
  };
};

